import React, { useState } from "react";
import { Box, Stack, Typography, Chip, IconButton, Menu, MenuItem } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowRight, FiClock, FiMoreVertical, FiArchive, FiTrash2, FiUser } from "react-icons/fi";
import {
  T,
  PRI_BG, PRI_TEXT, PRI_DOT, PRI_LABEL,
  STATUS_LABEL, STATUS_BG, STATUS_TEXT,
} from "../../constants/todoTokens";
import { isTaskOverdue } from "./helpers";
import AssigneeGroup from "../../../../components/AssigneeGroup";
import { visibleAssignees, isSoloView } from "../../../../components/AssigneeGroup/utils";

function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
}

const STATUS_META = Object.keys(STATUS_LABEL).reduce((acc, k) => {
  acc[k] = { label: STATUS_LABEL[k], bg: STATUS_BG[k], text: STATUS_TEXT[k] };
  return acc;
}, {});

const FullTaskCard = ({ task, onClick, onArchive, onDelete, index, isAdmin, currentUserId, onAssigneeStatusChanged }) => {
  const overdue = isTaskOverdue(task);
  const canAct = isAdmin || String(task.assignedBy) === String(currentUserId);
  const st = STATUS_META[task.status] || STATUS_META[1];
  const priDot = overdue ? "#EF4444" : (PRI_DOT[task.priority] || "#94A3B8");
  const priBg = PRI_BG[task.priority] || "#F1F5F9";
  const priTxt = PRI_TEXT[task.priority] || "#475569";
  const priLabel = PRI_LABEL[task.priority] || "—";
  const stBg = overdue ? "#FFF0F0" : st.bg;
  const stTxt = overdue ? "#DC2626" : st.text;
  const stLabel = st.label;
  const solo = isSoloView(task, { currentUserId, isAdmin });
  const people = visibleAssignees(task, { currentUserId, isAdmin });
  const doneCount = people.filter((p) => p.status === 3).length;
  const deadline = task.actualDeadlineDate
    ? new Date(task.actualDeadlineDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [hovered, setHovered] = useState(false);

  const desc = stripHtml(task.description);
  const creator = task.creatorName || task.creatorUserName || null;
  const supporter = task.supportName || null;
  const acknowledgedBy = task.acknowledgedByName || null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: index * 0.04 }}
        whileHover={{ x: 3 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "flex", flexDirection: "column", gap: 8,
          padding: "14px 16px",
          background: "#fff",
          border: `1px solid ${T.border}`,
          borderLeft: `4px solid ${priDot}`,
          borderRadius: 12,
          cursor: "pointer",
          boxShadow: T.shadow.sm,
          transition: "box-shadow 0.15s",
          position: "relative",
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Stack direction="column" gap={0.3} flex={1} onClick={() => onClick(task)}>
            <Stack direction="row" alignItems="center" spacing={0.8}>
              {task.taskNo && (
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.primary, letterSpacing: "0.4px" }}>
                  #{task.taskNo}
                </Typography>
              )}
              {task.serialNo && (
                <Box sx={{
                  px: "5px", py: "1px", borderRadius: "4px",
                  bgcolor: "#F0F9FF", border: "1px solid #BAE6FD",
                }}>
                  <Typography sx={{
                    fontSize: "9px", fontWeight: 700, color: "#0369A1",
                    fontFamily: "ui-monospace, SFMono-Regular, monospace", letterSpacing: "0.04em", lineHeight: 1.5,
                  }}>
                    {task.serialNo}
                  </Typography>
                </Box>
              )}
            </Stack>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: T.text, lineHeight: 1.4 }}>
              {task.title || "Untitled"}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            {canAct && (
              <IconButton
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                sx={{ width: 24, height: 24, borderRadius: "6px", color: "#94A3B8", "&:hover": { bgcolor: "#F1F5F9", color: "#475569" } }}
              >
                <FiMoreVertical size={13} />
              </IconButton>
            )}
            <FiArrowRight size={14} color={T.textMuted} style={{ flexShrink: 0, marginTop: 2, cursor: "pointer" }} onClick={() => onClick(task)} />
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" onClick={() => onClick(task)}>
          <Chip label={priLabel} size="small" sx={{ bgcolor: priBg, color: priTxt, fontWeight: 700, fontSize: "10px", height: 20, borderRadius: "5px" }} />
          <Chip label={stLabel} size="small" sx={{ bgcolor: stBg, color: stTxt, fontWeight: 700, fontSize: "10px", height: 20, borderRadius: "5px" }} />
          {deadline && (
            <Stack direction="row" alignItems="center" gap="4px">
              <FiClock size={10} color={T.textMuted} />
              <Typography sx={{ fontSize: "11px", color: T.textMuted, fontWeight: 500 }}>{deadline}</Typography>
            </Stack>
          )}
          {people.length > 0 && (
            <Stack direction="row" alignItems="center" gap="5px" onClick={(e) => e.stopPropagation()}>
              <AssigneeGroup
                task={task}
                taskId={task._id}
                assignedBy={task.assignedBy}
                size={18}
                max={3}
                placement="top"
                isAdmin={isAdmin}
                currentUserId={currentUserId}
                onChanged={onAssigneeStatusChanged}
              />
              <Typography sx={{ fontSize: "11px", color: T.textSub, fontWeight: 500 }}>
                {solo ? "You" : people.length === 1 ? people[0].name : `${doneCount}/${people.length} done`}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Hover-revealed description + creator */}
        <AnimatePresence>
          {hovered && (desc || creator) && (
            <motion.div
              key="hover-detail"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              style={{ overflow: "hidden" }}
              onClick={() => onClick(task)}
            >
              <Box sx={{
                mt: "2px", pt: "10px",
                borderTop: `1px solid ${T.borderLight}`,
                display: "flex", flexDirection: "column", gap: "6px",
              }}>
                {desc && (
                  <Typography sx={{
                    fontSize: "11px", color: T.textSub, lineHeight: 1.6,
                    display: "-webkit-box", WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical", overflow: "hidden",
                  }}>
                    {desc}
                  </Typography>
                )}
                {supporter && (
                  <Stack direction="row" alignItems="center" gap="5px">
                    <FiUser size={10} color={T.textMuted} />
                    <Typography sx={{ fontSize: "10px", color: T.textMuted, fontWeight: 500 }}>
                      Support <Box component="span" sx={{ fontWeight: 700, color: T.textSub }}>{supporter}</Box>
                    </Typography>
                  </Stack>
                )}
                {creator && (
                  <Stack direction="row" alignItems="center" gap="5px">
                    <FiUser size={10} color={T.textMuted} />
                    <Typography sx={{ fontSize: "10px", color: T.textMuted, fontWeight: 500 }}>
                      Assigned by <Box component="span" sx={{ fontWeight: 700, color: T.textSub }}>{creator}</Box>
                    </Typography>
                  </Stack>
                )}
                {acknowledgedBy && (
                  <Stack direction="row" alignItems="center" gap="5px">
                    <FiUser size={10} color={T.textMuted} />
                    <Typography sx={{ fontSize: "10px", color: T.textMuted, fontWeight: 500 }}>
                      Acknowledged by <Box component="span" sx={{ fontWeight: 700, color: T.textSub }}>{acknowledgedBy}</Box>
                    </Typography>
                  </Stack>
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: "10px", minWidth: 160,
            boxShadow: "0 8px 24px rgba(15,23,42,0.14)",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        {canAct && (
          <MenuItem
            onClick={() => { setMenuAnchor(null); onArchive?.(task); }}
            sx={{ fontSize: "13px", gap: 1.2, py: 1, color: "#D97706", fontFamily: "'Inter', sans-serif", fontWeight: 500, "&:hover": { bgcolor: "#FFFBEB" } }}
          >
            <FiArchive size={13} />
            Archive
          </MenuItem>
        )}
        {canAct && (
          <MenuItem
            onClick={() => { setMenuAnchor(null); onDelete?.(task); }}
            sx={{ fontSize: "13px", gap: 1.2, py: 1, color: "#DC2626", fontFamily: "'Inter', sans-serif", fontWeight: 500, "&:hover": { bgcolor: "#FEF2F2" } }}
          >
            <FiTrash2 size={13} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default React.memo(FullTaskCard);
